import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  FormField,
  Modal,
  Select,
  Spinner,
} from '@chopchop-se/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SHIPMENT_TEMPLATES, type Shipment } from '@warehouse/shared';
import { ArrowLeft, FileDown, Printer, Truck } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import { PageHeader } from '../../../components/PageHeader';
import { formatDate, formatDateTime, formatQty } from '../../../lib/format';
import { toastError, toastInfo, toastSuccess } from '../../../lib/notify';
import { useLiveQuery } from '../../../realtime/useLiveQuery';
import { pickingKeys, shipmentsApi } from '../api';
import { PackingSlipPrintView } from '../components/PickListPrintView';
import { ShipmentStatusBadge } from '../components/StatusBadge';

/**
 * One shipment: its pallets, its carrier booking and its documents.
 *
 * "Boka transport" is available from order status `pagaende` — that is the
 * pre-booking Victor asked for, because the carrier needs lead time and waiting
 * for packing to finish misses the truck.
 */
export function ShipmentDetailPage(): ReactNode {
  const { t } = useTranslation('pickingShipping');
  const { t: tc } = useTranslation('common');
  const { shipmentId = '' } = useParams();
  const queryClient = useQueryClient();

  const [booking, setBooking] = useState(false);
  const [slip, setSlip] = useState(false);
  const [palletCode, setPalletCode] = useState('');
  const [weight, setWeight] = useState('');

  const shipment = useQuery({
    queryKey: pickingKeys.shipment(shipmentId),
    queryFn: () => shipmentsApi.get(shipmentId),
    enabled: shipmentId.length > 0,
  });
  useLiveQuery(['shipments', 'receptions'], pickingKeys.shipment(shipmentId));

  const invalidate = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['shipments'] });
  };

  const assign = useMutation({
    mutationFn: () =>
      shipmentsApi.assignPallet(shipmentId, {
        code: palletCode.trim(),
        weight: weight.trim() || null,
        packageCount: 1,
      }),
    onSuccess: async () => {
      toastSuccess(t('packing.palletAdded'));
      setPalletCode('');
      setWeight('');
      await invalidate();
    },
    onError: (error: unknown) => {
      toastError(error);
    },
  });

  const removePallet = useMutation({
    mutationFn: (palletId: string) => shipmentsApi.removePallet(shipmentId, palletId),
    onSuccess: async () => {
      toastSuccess(t('packing.palletRemoved'));
      await invalidate();
    },
    onError: (error: unknown) => {
      toastError(error);
    },
  });

  const pack = useMutation({
    mutationFn: () => shipmentsApi.pack(shipmentId),
    onSuccess: async () => {
      toastSuccess(t('packing.completed'));
      await invalidate();
    },
    onError: (error: unknown) => {
      toastError(error);
    },
  });

  const cancel = useMutation({
    mutationFn: () => shipmentsApi.cancel(shipmentId),
    onSuccess: async () => {
      toastSuccess(t('shipments.cancelledToast'));
      await invalidate();
    },
    onError: (error: unknown) => {
      toastError(error);
    },
  });

  const ship = useMutation({
    mutationFn: () => shipmentsApi.ship(shipmentId),
    onSuccess: async () => {
      toastSuccess(t('shipments.shippedToast'));
      await invalidate();
    },
    onError: (error: unknown) => {
      toastError(error);
    },
  });

  const printLabels = useMutation({
    mutationFn: () => shipmentsApi.printLabels(shipmentId),
    onSuccess: (result) => {
      if (result.routed) toastSuccess(t('paper.printQueued'));
      else toastInfo(t('paper.printNotRouted'));
    },
    onError: (error: unknown) => {
      toastError(error);
    },
  });

  if (shipment.isLoading) return <Spinner />;
  if (!shipment.data) return null;

  const data: Shipment = shipment.data;
  const editable = data.status === 'draft' || data.status === 'booked';

  return (
    <>
      <PageHeader
        breadcrumb={
          <Link to="/orders/shipments" className="inline-flex items-center gap-1 hover:text-ink">
            <ArrowLeft className="size-3" aria-hidden /> {t('shipments.title')}
          </Link>
        }
        title={data.shipmentNo}
        subtitle={`${data.orderNo} · ${data.restaurantName} · ${formatDate(data.deliveryDate)}`}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <ShipmentStatusBadge status={data.status} />
            <Badge tone={data.type === 'own' ? 'accent' : 'neutral'} size="sm">
              {t(`shipments.types.${data.type}`)}
            </Badge>
            {data.status === 'draft' ? (
              <Button
                size="sm"
                loading={pack.isPending}
                onClick={() => {
                  pack.mutate();
                }}
              >
                {t('packing.complete')}
              </Button>
            ) : null}
            {editable ? (
              <Button
                size="sm"
                icon={<Truck className="size-4" />}
                onClick={() => {
                  setBooking(true);
                }}
              >
                {data.status === 'booked' ? t('shipments.rebook') : t('shipments.book')}
              </Button>
            ) : null}
            {data.status === 'booked' ? (
              <Button
                variant="secondary"
                size="sm"
                loading={ship.isPending}
                onClick={() => {
                  if (globalThis.confirm(t('shipments.shipConfirm', { shipmentNo: data.shipmentNo }))) {
                    ship.mutate();
                  }
                }}
              >
                {t('shipments.ship')}
              </Button>
            ) : null}
            {editable ? (
              <Button
                variant="ghost"
                size="sm"
                loading={cancel.isPending}
                onClick={() => {
                  if (globalThis.confirm(t('shipments.cancelConfirm', { shipmentNo: data.shipmentNo }))) {
                    cancel.mutate();
                  }
                }}
              >
                {t('shipments.cancel')}
              </Button>
            ) : null}
          </div>
        }
      />

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <span className="text-sm font-semibold">{t('packing.pallets')}</span>
          </CardHeader>
          <CardBody>
            {data.pallets.length === 0 ? (
              <p className="mb-4 text-sm text-ink-muted">{t('packing.noPallets')}</p>
            ) : (
              <ul className="mb-4 flex flex-col gap-2" data-testid="shipment-pallets">
                {data.pallets.map((pallet) => (
                  <li
                    key={pallet.id}
                    className="flex items-center gap-3 rounded-lg border border-surface-border px-3 py-2 text-sm"
                  >
                    <span className="font-mono font-semibold">{pallet.lpn}</span>
                    {pallet.ssccAlias ? (
                      <Badge tone="neutral" size="sm">
                        SSCC {pallet.ssccAlias}
                      </Badge>
                    ) : null}
                    <span className="text-ink-muted">
                      {pallet.weight ? `${formatQty(pallet.weight)} kg` : ''}
                    </span>
                    <span className="ml-auto text-xs text-ink-muted">
                      {t('packing.packageCount')}: {pallet.packageCount}
                    </span>
                    {editable ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          if (globalThis.confirm(t('packing.removePalletConfirm', { lpn: pallet.lpn }))) {
                            removePallet.mutate(pallet.palletId);
                          }
                        }}
                      >
                        {tc('actions.remove')}
                      </Button>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}

            {editable ? (
              <div className="flex flex-wrap items-end gap-2">
                <FormField
                  label={t('packing.scanPallet')}
                  className="min-w-48 flex-1"
                  value={palletCode}
                  onChange={(event) => {
                    setPalletCode(event.target.value);
                  }}
                />
                <FormField
                  label={t('packing.weight')}
                  className="w-32"
                  inputMode="decimal"
                  value={weight}
                  onChange={(event) => {
                    setWeight(event.target.value);
                  }}
                />
                <Button
                  loading={assign.isPending}
                  disabled={palletCode.trim().length === 0}
                  onClick={() => {
                    assign.mutate();
                  }}
                >
                  {t('packing.addPallet')}
                </Button>
              </div>
            ) : null}
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <span className="text-sm font-semibold">{t('shipments.title')}</span>
          </CardHeader>
          <CardBody>
            <dl className="grid grid-cols-2 gap-y-2 text-sm">
              <Detail label={t('shipments.template')} value={data.templateName ?? '—'} />
              <Detail label={t('shipments.consignmentNo')} value={data.consignmentNo ?? '—'} mono />
              <Detail
                label={t('shipments.pickupDate')}
                value={data.pickupDate ? formatDate(data.pickupDate) : '—'}
              />
              <Detail
                label={t('shipments.palletCountEstimate')}
                value={data.palletCountEstimate ? String(data.palletCountEstimate) : '—'}
              />
              <Detail label={t('shipments.bookedAt')} value={formatDateTime(data.bookedAt)} />
              <Detail label={t('shipments.shippedAt')} value={formatDateTime(data.shippedAt)} />
              <Detail label={t('shipments.deliveredAt')} value={formatDateTime(data.deliveredAt)} />
            </dl>

            {data.trackingUrl ? (
              <a
                className="mt-3 inline-block text-sm text-brand-600 underline"
                href={data.trackingUrl}
                target="_blank"
                rel="noreferrer"
              >
                {t('shipments.tracking')}
              </a>
            ) : null}

            <p className="mt-4 text-xs text-ink-muted">{t('packing.qrHint')}</p>
            <code className="mt-1 block break-all rounded bg-surface-subtle p-2 text-[11px]">
              {data.receiveUrl}
            </code>

            <div className="mt-4 flex flex-wrap gap-2">
              <Button
                variant="secondary"
                size="sm"
                icon={<Printer className="size-4" />}
                onClick={() => {
                  setSlip(true);
                }}
              >
                {t('packing.printPackingSlip')}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                loading={printLabels.isPending}
                onClick={() => {
                  printLabels.mutate();
                }}
              >
                {t('packing.printLabels')}
              </Button>
            </div>
          </CardBody>
        </Card>

        <Card className="lg:col-span-3">
          <CardHeader>
            <span className="text-sm font-semibold">{t('shipments.documents')}</span>
          </CardHeader>
          <CardBody>
            {data.documents.length === 0 ? (
              <p className="text-sm text-ink-muted">{t('shipments.noDocuments')}</p>
            ) : (
              <ul className="flex flex-col gap-2" data-testid="shipment-documents">
                {data.documents.map((doc) => (
                  <li key={doc.id} className="flex items-center gap-3 text-sm">
                    <FileDown className="size-4 text-ink-muted" aria-hidden />
                    <a
                      className="font-mono text-brand-600 underline"
                      href={shipmentsApi.documentUrl(data.id, doc.id)}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {doc.fileName}
                    </a>
                    <Badge tone="neutral" size="sm">
                      {doc.format.toUpperCase()}
                    </Badge>
                    <span className="ml-auto text-xs text-ink-muted">
                      {formatDateTime(doc.createdAt)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>
      </div>

      {booking ? (
        <BookModal
          shipment={data}
          onClose={() => {
            setBooking(false);
          }}
          onDone={async () => {
            setBooking(false);
            await invalidate();
          }}
        />
      ) : null}

      {slip ? (
        <PackingSlipModal
          shipmentId={shipmentId}
          onClose={() => {
            setSlip(false);
          }}
        />
      ) : null}
    </>
  );
}

function Detail({ label, value, mono }: { label: string; value: string; mono?: boolean }): ReactNode {
  return (
    <>
      <dt className="text-ink-muted">{label}</dt>
      <dd className={mono ? 'font-mono text-xs' : ''}>{value}</dd>
    </>
  );
}

function BookModal({
  shipment,
  onClose,
  onDone,
}: {
  shipment: Shipment;
  onClose: () => void;
  onDone: () => Promise<void>;
}): ReactNode {
  const { t } = useTranslation('pickingShipping');
  const { t: tc } = useTranslation('common');
  const [templateName, setTemplateName] = useState(shipment.templateName ?? '');
  const [pickupDate, setPickupDate] = useState(shipment.pickupDate ?? '');
  const [palletCount, setPalletCount] = useState(
    String(shipment.palletCountEstimate ?? Math.max(shipment.pallets.length, 1)),
  );

  const isPreBooking = shipment.orderStatus === 'pagaende';

  const book = useMutation({
    mutationFn: () =>
      shipmentsApi.book(shipment.id, {
        templateName: templateName || null,
        pickupDate: pickupDate || null,
        palletCountEstimate: Number(palletCount) || 1,
      }),
    onSuccess: async () => {
      toastSuccess(t('shipments.bookedToast'));
      await onDone();
    },
    onError: (error: unknown) => {
      toastError(error);
    },
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={isPreBooking ? t('shipments.preBooking.title') : t('shipments.bookDialog.title')}
      width={520}
    >
      <div className="flex flex-col gap-4">
        {isPreBooking ? (
          <p className="rounded-lg bg-brand-50 p-3 text-sm text-brand-900">
            {t('shipments.preBooking.hint')}
          </p>
        ) : null}

        <Select
          label={t('shipments.bookDialog.template')}
          value={templateName}
          placeholder={t('shipments.bookDialog.templateAuto')}
          options={SHIPMENT_TEMPLATES.map((name) => ({
            value: name,
            label: name === 'default-template' ? t('shipments.bookDialog.templateAuto') : name,
          }))}
          onChange={(event) => {
            setTemplateName(event.target.value);
          }}
        />

        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">{t('shipments.bookDialog.pickupDate')}</span>
          <input
            type="date"
            className="rounded-lg border border-surface-border px-3 py-2"
            value={pickupDate}
            onChange={(event) => {
              setPickupDate(event.target.value);
            }}
          />
        </label>

        <FormField
          label={t('shipments.bookDialog.palletCountEstimate')}
          inputMode="numeric"
          value={palletCount}
          onChange={(event) => {
            setPalletCount(event.target.value);
          }}
        />

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            {tc('actions.cancel')}
          </Button>
          <Button
            loading={book.isPending}
            onClick={() => {
              book.mutate();
            }}
          >
            {t('shipments.bookDialog.submit')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function PackingSlipModal({
  shipmentId,
  onClose,
}: {
  shipmentId: string;
  onClose: () => void;
}): ReactNode {
  const { t } = useTranslation('pickingShipping');
  const { t: tc } = useTranslation('common');
  const document = useQuery({
    queryKey: pickingKeys.packingSlip(shipmentId),
    queryFn: () => shipmentsApi.packingSlip(shipmentId),
  });

  return (
    <Modal open onClose={onClose} title={t('packing.packingSlip')} width={900}>
      <div className="flex flex-col gap-4">
        {document.isLoading ? <Spinner /> : null}
        {document.data ? <PackingSlipPrintView document={document.data} /> : null}
        <div className="flex justify-end gap-2 print:hidden">
          <Button variant="ghost" onClick={onClose}>
            {tc('actions.close')}
          </Button>
          <Button
            onClick={() => {
              globalThis.print();
            }}
          >
            {tc('actions.print')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
